import { PAYWALL_OFFER_SUBTYPES } from "@circle-react/helpers/paywallOfferHelpers";
import {
  buildItem,
  buildPurchaseEcommerce,
  pushPurchaseEvent,
} from "@circle-react/helpers/paywalls/ga4DataLayer";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";
import { paywallPriceTypes } from "@circle-react/types/PaywallPrice";
import type { MutableRefObject } from "react";

import {
  getMainStep,
  getStepsByStatus,
} from "@/react/components/Paywalls/CheckoutWithSession/helpers/SessionStepQueryHelper";
import { amount as amountFromMinorUnits } from "@/react/helpers/paywalls/currencies";
import { browserSessionStorage } from "@/react/lib/browserStorage";
import type { PaywallCheckoutPreview } from "@/react/types/Paywall";
import type {
  CheckoutSession,
  CheckoutSessionStep,
} from "@/react/types/Paywalls/Checkout/Session";
import { checkoutSessionStepStatuses } from "@/react/types/Paywalls/Checkout/Session";
import type {
  Ga4Paywall,
  Ga4PurchaseEcommerce,
  Ga4Redemption,
} from "@/react/types/Paywalls/Ga4DataLayer";

// Attempt callbacks and completed-step fallback share tab-local dedup.
// Upgrades use completed step charges; Add-ons use attempt previews.
// sessionStorage key for the fired-step set, scoped to a single checkout
// session. Stores a JSON-serialised array of step IDs that have already had a
// purchase event pushed in this browser tab.
//
// Key format: paywalls:ga4_purchase_fired:<session.id>
// Lifetime:   browser tab (sessionStorage clears when the tab closes).
const storageKey = (sessionId: number): string =>
  `paywalls:ga4_purchase_fired:${sessionId}`;

const getUpgradeOffer = (step: CheckoutSessionStep) =>
  step.offers.find(
    offer => offer.paywall_offer?.subtype === PAYWALL_OFFER_SUBTYPES.UPGRADE,
  );

const isUpgradeStep = (step: CheckoutSessionStep): boolean =>
  getUpgradeOffer(step) !== undefined;

const readFiredStepIds = (sessionId: number): Set<number> => {
  try {
    const raw = browserSessionStorage.getItem(storageKey(sessionId));
    return new Set<number>(raw ? (JSON.parse(raw) as number[]) : []);
  } catch {
    console.warn(
      "[ga4PurchaseHelper] sessionStorage read failed; using in-memory dedup only.",
    );
    return new Set<number>();
  }
};

const persistFiredStepIds = (
  sessionId: number,
  newIds: ReadonlySet<number>,
): void => {
  if (newIds.size === 0) return;
  try {
    const existing = readFiredStepIds(sessionId);
    for (const id of newIds) existing.add(id);
    browserSessionStorage.setItem(
      storageKey(sessionId),
      JSON.stringify([...existing]),
    );
  } catch {
    console.warn(
      "[ga4PurchaseHelper] sessionStorage write failed; in-memory dedup only.",
    );
  }
};

// markStepFiredForDedup — records a step ID in both the in-memory ref and
// sessionStorage so subsequent call sites (ConfirmationPage, useGoToNextStep)
// skip it via the two-layer dedup guard.
const markStepFiredForDedup = (
  sessionId: number,
  stepId: number,
  ga4FiredStepIdsRef: MutableRefObject<Set<number>>,
): void => {
  ga4FiredStepIdsRef.current.add(stepId);
  persistFiredStepIds(sessionId, new Set([stepId]));
};

// Ordinary purchase onComplete path; records the step in both dedup layers.
export const pushPurchaseEventFromOffer = ({
  ga4Paywall,
  selectedPrice,
  preview,
  subscriptionId,
  paymentIntentId,
  invoiceId,
  sessionId,
  stepId,
  ga4FiredStepIdsRef,
}: {
  ga4Paywall: Ga4Paywall;
  selectedPrice: PaywallPrice;
  preview: PaywallCheckoutPreview | undefined;
  subscriptionId?: string;
  paymentIntentId?: string;
  invoiceId?: string;
  sessionId: number;
  stepId: number;
  ga4FiredStepIdsRef: MutableRefObject<Set<number>>;
}): void => {
  const ecommerce = buildPurchaseEcommerceFromOffer({
    ga4Paywall,
    selectedPrice,
    preview,
    subscriptionId,
    paymentIntentId,
    invoiceId,
  });
  if (!ecommerce) return;

  pushPurchaseEvent(ecommerce);
  markStepFiredForDedup(sessionId, stepId, ga4FiredStepIdsRef);
};

// Use the checkout preview amount and successful-attempt identity for ordinary
// purchases. Upgrade purchases use the completed step's linked charge instead.
const buildPurchaseEcommerceFromOffer = ({
  ga4Paywall,
  selectedPrice,
  preview,
  subscriptionId,
  paymentIntentId,
  invoiceId,
}: {
  ga4Paywall: Ga4Paywall;
  selectedPrice: PaywallPrice;
  preview: PaywallCheckoutPreview | undefined;
  subscriptionId?: string;
  paymentIntentId?: string;
  invoiceId?: string;
}): Ga4PurchaseEcommerce | undefined => {
  const isSubscriptionOrInstallments =
    selectedPrice.price_type === paywallPriceTypes.subscription ||
    selectedPrice.price_type === paywallPriceTypes.installments;

  const transactionId = isSubscriptionOrInstallments
    ? subscriptionId
    : (paymentIntentId ?? invoiceId);

  const currencyCode = preview?.currency?.code ?? undefined;

  if (!transactionId || !preview || !currencyCode) {
    console.error(
      "[ga4PurchaseHelper] buildPurchaseEcommerceFromOffer: required fields missing — skipping push.",
      { ga4Paywall, selectedPrice, transactionId, currencyCode },
    );
    return undefined;
  }

  // Resolve coupon redemption from the preview. Both code and discount_amount
  // must be present; otherwise omit entirely (spec Decision 6).
  const couponCode = preview.paywall_coupon?.code;
  const couponApplication = preview.coupon_applications?.applications?.[0];
  const discountAmount = couponApplication?.discount_amount;
  const discountAmountDecimal = couponApplication?.discount_amount_decimal;
  const redemption: Ga4Redemption | undefined =
    couponCode && discountAmount != null && discountAmountDecimal != null
      ? {
          code: couponCode,
          discount_amount: discountAmount,
          discount_amount_decimal: discountAmountDecimal,
        }
      : undefined;

  // Ga4PaywallPrice shape expected by buildItem.
  // amount_decimal and value come from backend-provided *_decimal fields
  // (via Currency#amount) — no frontend unit conversion needed.
  const ga4Currency = {
    code: currencyCode,
    smallest_unit: preview.currency.smallest_unit,
  };
  const ga4Price = {
    amount: selectedPrice.amount,
    amount_decimal: preview.paywall_price.amount_decimal,
    price_type: selectedPrice.price_type,
    interval_type: selectedPrice.interval_type,
    interval: selectedPrice.interval,
    interval_count: selectedPrice.interval_count,
    currency: ga4Currency,
  };

  const ecommerce: Ga4PurchaseEcommerce = {
    currency: currencyCode,
    items: [buildItem(ga4Paywall, ga4Price, redemption)],
    transaction_id: transactionId,
    value: preview.amount_due_now_decimal,
  };

  if (redemption) {
    ecommerce.coupon = redemption.code;
    ecommerce.discount = redemption.discount_amount_decimal;
  }

  return ecommerce;
};

// Upgrade charges are subscription-linked, so the session serializer omits
// amount_decimal and paywall_price. Use the charge's minor-unit amount and the
// retained main subscription's currency instead of preview/list-price amounts.
const buildUpgradePurchaseEcommerce = (
  session: CheckoutSession,
  step: CheckoutSessionStep,
): Ga4PurchaseEcommerce | undefined => {
  const charge = step.community_member_charge;
  const subscription = getMainStep(
    session.steps,
  )?.community_member_subscription;
  const upgradeOffer = getUpgradeOffer(step);
  const transactionId = charge?.processor_id ?? charge?.invoice_processor_id;
  const currency = upgradeOffer?.paywall.currency;
  const subscriptionId = subscription?.processor_id;

  if (
    !charge ||
    charge.amount <= 0 ||
    !transactionId ||
    !currency ||
    !subscriptionId ||
    !upgradeOffer
  ) {
    return undefined;
  }

  const value = amountFromMinorUnits(currency, charge.amount);
  const itemName =
    upgradeOffer.paywall.display_name ?? upgradeOffer.paywall.name ?? "";

  return {
    currency: currency.code,
    items: [
      {
        item_id: String(upgradeOffer.paywall.id),
        item_name: itemName,
        price: value,
        quantity: 1,
      },
    ],
    subscription_id: subscriptionId,
    transaction_id: transactionId,
    value,
  };
};

// firePurchaseForCompletedSteps — pushes a GA4 purchase event for each
// completed step that has not been fired yet.
//
// Mark only emitted purchases so incomplete charge data can be retried.
export const firePurchaseForCompletedSteps = (
  session: CheckoutSession | null,
  ga4FiredStepIdsRef: MutableRefObject<Set<number>>,
): void => {
  const steps = getStepsByStatus(session?.steps ?? [], [
    checkoutSessionStepStatuses.completed,
  ]);

  const persisted = session ? readFiredStepIds(session.id) : new Set<number>();

  // Collect newly-fired step IDs so we can batch-write to sessionStorage once
  // after the loop instead of calling readFiredStepIds on every iteration.
  const newlyFired = new Set<number>();

  steps
    .filter(
      step =>
        !ga4FiredStepIdsRef.current.has(step.id) && !persisted.has(step.id),
    )
    .forEach(step => {
      const ecommerce = isUpgradeStep(step)
        ? session
          ? buildUpgradePurchaseEcommerce(session, step)
          : undefined
        : buildPurchaseEcommerce(step);
      if (!ecommerce) return;

      pushPurchaseEvent(ecommerce);
      ga4FiredStepIdsRef.current.add(step.id);
      newlyFired.add(step.id);
    });

  // Single sessionStorage write for the whole batch — avoids N reads inside the loop.
  if (session) persistFiredStepIds(session.id, newlyFired);
};
